import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Logo } from '../components/Logo';
import PageTitle from '../components/PageTitle';
import { 
  ChevronLeft,
  Clock,
  Zap,
  Target,
  Flame,
  Heart,
  Award,
  ArrowRight
} from 'lucide-react';

const TheMethodPage = () => {
  return (
    <div className="min-h-screen bg-[#090D0F]">
      <PageTitle title="The Lagree Method" description="Learn about the revolutionary Lagree Method - NOT Pilates. A high-intensity, low-impact workout." />
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo linkTo="/" size="default" />
          <div className="flex items-center gap-4">
            <Link to="/classes">
              <Button variant="ghost" className="text-zinc-400 hover:text-white hidden md:flex">
                Browse Classes
              </Button>
            </Link>
            <Link to="/shop">
              <Button className="bg-teal-400 text-black font-bold hover:bg-teal-300">
                Shop Machines
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section className="relative py-24 px-6 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-teal-400/5 to-transparent" />
          <div className="max-w-4xl mx-auto text-center relative">
            <span className="text-teal-400 font-bold tracking-widest text-sm mb-4 block">THE LAGREE METHOD</span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              NOT PILATES.<br />
              <span className="text-teal-400">LAGREE.</span>
            </h1>
            <p className="text-xl text-zinc-400 max-w-2xl mx-auto mb-8">
              A revolutionary low-impact, high-intensity workout that transforms your body through slow, controlled movements on the patented Megaformer machine.
            </p>
            <p className="text-zinc-500">
              Created by fitness visionary <span className="text-white font-semibold">Sebastien Lagree</span> in Los Angeles
            </p>
          </div>
        </section>

        {/* What Makes It Different */}
        <section className="py-20 px-6 bg-[#0A0D0F]">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-16">WHAT MAKES LAGREE DIFFERENT</h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-[#121416] border border-zinc-800 p-8 rounded-sm">
                <div className="w-12 h-12 bg-teal-400/10 rounded-sm flex items-center justify-center mb-6">
                  <Clock className="w-6 h-6 text-teal-400" />
                </div>
                <h3 className="text-xl font-bold mb-4">Time Under Tension</h3>
                <p className="text-zinc-400">
                  Every movement follows a slow 4-count tempo, keeping muscles engaged longer. This sustained activation triggers deep muscle fatigue and transformation you simply can't achieve with faster movements.
                </p>
              </div>

              <div className="bg-[#121416] border border-zinc-800 p-8 rounded-sm">
                <div className="w-12 h-12 bg-teal-400/10 rounded-sm flex items-center justify-center mb-6">
                  <Zap className="w-6 h-6 text-teal-400" />
                </div>
                <h3 className="text-xl font-bold mb-4">Effective Resistance</h3>
                <p className="text-zinc-400">
                  The Megaformer's spring-loaded system provides variable resistance throughout each movement. Unlike weights, the resistance adapts to your body, making every rep maximally effective.
                </p>
              </div>

              <div className="bg-[#121416] border border-zinc-800 p-8 rounded-sm">
                <div className="w-12 h-12 bg-teal-400/10 rounded-sm flex items-center justify-center mb-6">
                  <Target className="w-6 h-6 text-teal-400" />
                </div>
                <h3 className="text-xl font-bold mb-4">Slow-Twitch Activation</h3>
                <p className="text-zinc-400">
                  Lagree targets slow-twitch muscle fibers responsible for endurance and lean muscle definition. The result: long, sculpted muscles without bulk.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* The Megaformer */}
        <section className="py-20 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <span className="text-teal-400 font-bold tracking-widest text-sm mb-4 block">THE MACHINE</span>
                <h2 className="text-3xl font-bold mb-6">THE MEGAFORMER</h2>
                <p className="text-zinc-400 mb-6">
                  The patented Megaformer is the heart of the Lagree Method. Unlike a traditional Pilates reformer, it features:
                </p>
                <ul className="space-y-4">
                  {[
                    'A moving carriage that glides on precision rails',
                    'Adjustable spring tension for all fitness levels',
                    'Multiple platforms and handles for 300+ exercises',
                    'Compact footprint perfect for home studios'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-sm bg-teal-400/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-teal-400 text-sm font-bold">{i + 1}</span>
                      </div>
                      <span className="text-zinc-300">{item}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/shop">
                  <Button className="mt-8 bg-teal-400 text-black font-bold hover:bg-teal-300">
                    Explore Machines
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
              <div className="bg-gradient-to-br from-zinc-800 to-zinc-900 aspect-square rounded-sm flex items-center justify-center">
                <img 
                  src="https://images.unsplash.com/photo-1638805981949-362f5964521e?q=85&w=800&auto=format&fit=crop"
                  alt="Lagree Megaformer"
                  className="w-full h-full object-cover rounded-sm"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Key Terminology */}
        <section className="py-20 px-6 bg-[#0A0D0F]">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-4">LAGREE GLOSSARY</h2>
            <p className="text-zinc-500 text-center mb-12 max-w-2xl mx-auto">
              Master the language of Lagree. These terms will help you follow along in class and understand the method.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { term: 'Carriage', definition: 'The moving platform that glides back and forth on the machine rails' },
                { term: 'Springs', definition: 'Color-coded resistance bands that attach to the carriage (yellow = lighter, red = heavier)' },
                { term: 'Front Platform', definition: 'The stationary platform at the front of the machine' },
                { term: 'Back Platform', definition: 'The stationary platform at the rear, often used for standing exercises' },
                { term: 'Shake', definition: 'The muscle trembling you experience during slow, controlled movements - this means it\'s working!' },
                { term: 'Elevator Lunge', definition: 'Signature Lagree exercise: a slow lunge where the back knee drops and rises like an elevator' },
                { term: 'Catfish', definition: 'Core exercise performed face-down, driving knees toward chest' },
                { term: 'Scrambled Eggs', definition: 'Intense glute exercise with circular hip movements on the carriage' }
              ].map((item, i) => (
                <div key={i} className="bg-[#121416] border border-zinc-800 p-6 rounded-sm">
                  <h4 className="text-teal-400 font-bold mb-2">{item.term}</h4>
                  <p className="text-zinc-400 text-sm">{item.definition}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-20 px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-16">THE RESULTS</h2>
            
            <div className="grid md:grid-cols-4 gap-6">
              {[
                { icon: Flame, stat: '500+', label: 'Calories burned per class' },
                { icon: Heart, stat: '2x', label: 'More effective than Pilates' },
                { icon: Target, stat: '300+', label: 'Unique exercises possible' },
                { icon: Award, stat: '100%', label: 'Low impact, joint-safe' }
              ].map((item, i) => (
                <div key={i} className="text-center">
                  <div className="w-16 h-16 bg-teal-400/10 rounded-sm flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-8 h-8 text-teal-400" />
                  </div>
                  <p className="text-3xl font-bold text-teal-400 mb-2">{item.stat}</p>
                  <p className="text-zinc-400 text-sm">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 px-6 bg-gradient-to-b from-[#0A0D0F] to-[#090D0F]">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">READY TO EXPERIENCE THE SHAKE?</h2>
            <p className="text-zinc-400 mb-8">
              Start your Lagree journey today. Browse our class library or get your own Megaformer for the ultimate at-home experience.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/classes">
                <Button className="w-full sm:w-auto bg-teal-400 text-black font-bold hover:bg-teal-300 px-8">
                  Browse Classes
                </Button>
              </Link>
              <Link to="/shop">
                <Button variant="outline" className="w-full sm:w-auto border-zinc-700 px-8">
                  Shop Machines
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-zinc-800 py-12 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <Logo linkTo="/" size="default" />
          <p className="text-zinc-500 text-sm mt-4">
            © 2026 Lagree MVMT. The Lagree Method and Megaformer are trademarks of Lagree Fitness.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TheMethodPage;
